/**
 * Home Dashboard - Tenant overview with project cards, feature discovery, and account setup
 * Enhanced with:
 * - Feature Discovery: Lists all available features from enabled plugins and baseline
 * - Help Center Integration: Quick access to help documentation
 * - Account Setup Steps: Checklist for missing tenant/project configuration
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { 
  listProjects, 
  createProject, 
  getProjectStats,
  type ProjectStats 
} from '../../api/projects';
import { listTenants } from '../../api/tenants';
import { listPolicyPacks, getPolicyPack, type PolicyPack } from '../../api/policy';
import { listSources } from '../../api/sources';
import { PolicyPackViewer } from '../Policy/PolicyPackViewer';
import { HelpCenter } from '../HelpCenter/HelpCenter';
import { SimplifiedDashboard } from '../../pages/SimplifiedDashboard';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useAuthContext } from '../Auth/AuthProvider';
import { usePermissions } from '../../hooks/usePermissions';
import { pluginRegistry } from '../../plugins/PluginRegistry';
import { getBaseUrl } from '../../utils/env';
import { formatNumber, formatFileSize } from '../../utils/numberFormatter';
import { 
  Plus,
  Database,
  FileText,
  Zap,
  CheckCircle,
  Clock,
  AlertCircle,
  Settings,
  Shield,
  Folder,
  TrendingUp,
  X,
  ChevronDown,
  ChevronRight,
  Edit3,
  HelpCircle,
  Rocket,
  Search,
  MessageSquare,
  Package,
  Layers,
  BarChart,
  Users,
  Briefcase,
  ListChecks,
  ExternalLink,
  ArrowRight,
} from 'lucide-react';

interface ProjectCardProps {
  project: any;
  stats: ProjectStats | null;
  statsLoading: boolean;
  onNavigate: (projectId: string) => void;
}

function ProjectCard({ project, stats, statsLoading, onNavigate }: ProjectCardProps) {
  return (
    <Card className="hover:shadow-lg transition-all cursor-pointer border-2 border-gray-200 hover:border-blue-400">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Folder className="h-5 w-5 text-blue-600" />
              <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">{project.name}</h3>
            </div>
            {project.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{project.description}</p>
            )}
          </div>
          <Button
            size="sm"
            onClick={() => onNavigate(project.id)}
            className="ml-2"
          >
            Open
          </Button>
        </div>

        {/* Stats Grid */}
        {statsLoading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-2.5 rounded-lg">
              <div className="flex items-center gap-1.5 mb-1">
                <FileText className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                <div className="text-[10px] text-gray-600 dark:text-gray-400 leading-tight">Artifacts</div>
              </div>
              <div className="text-base font-bold text-blue-900 dark:text-blue-300 leading-tight mb-0.5">{formatNumber(stats.total_artifacts)}</div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate">{formatFileSize(stats.total_artifact_size_mb * 1024 * 1024, { decimals: 1 })}</div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 p-2.5 rounded-lg">
              <div className="flex items-center gap-1.5 mb-1">
                <Database className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                <div className="text-[10px] text-gray-600 dark:text-gray-400 leading-tight">Entities</div>
              </div>
              <div className="text-base font-bold text-green-900 dark:text-green-300 leading-tight mb-0.5">{formatNumber(stats.total_entities)}</div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate">
                {formatNumber(Object.keys(stats.entities_by_type).length)} types
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 p-2.5 rounded-lg">
              <div className="flex items-center gap-1.5 mb-1">
                <Zap className="h-3.5 w-3.5 text-purple-600 flex-shrink-0" />
                <div className="text-[10px] text-gray-600 dark:text-gray-400 leading-tight">Jobs</div>
              </div>
              <div className="text-base font-bold text-purple-900 dark:text-purple-300 leading-tight mb-0.5">{formatNumber(stats.total_jobs)}</div>
              <div className="flex gap-1 text-[10px] leading-tight">
                <span className="text-green-600">{formatNumber(stats.completed_jobs)} ✓</span>
                <span className="text-red-600">{formatNumber(stats.failed_jobs)} ✗</span>
              </div>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900/20 p-2.5 rounded-lg">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="h-3.5 w-3.5 text-orange-600 flex-shrink-0" />
                <div className="text-[10px] text-gray-600 dark:text-gray-400 leading-tight">Sources</div>
              </div>
              <div className="text-base font-bold text-orange-900 dark:text-orange-300 leading-tight mb-0.5">{formatNumber(stats.total_sources)}</div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight truncate">{formatNumber(stats.total_chunks)} chunks</div>
            </div>
          </div>
        ) : null}

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-200">
          <div>
            Created {new Date(project.created_at).toLocaleDateString()}
          </div>
          {project.policy_pack_id && (
            <div className="flex items-center gap-1 text-blue-600">
              <Shield className="h-3 w-3" />
              Policy Pack
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export function HomeDashboard() {
  const { tenantId, tenant, setProjectId, setProjects, projectId, project, tenantConfig, hasCustomConfig } = useWorkspace();
  const { isAuthenticated } = useAuthContext();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const queryClient = useQueryClient();
  
  // Check if user should see simplified dashboard (Viewer or Writer without data management access)
  const shouldShowSimplified = !hasPermission('data_management.read') && 
                                (hasAnyPermission(['rfp.proposals.view', 'proposals.read']) || 
                                 hasPermission('proposals.write'));
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    slug: '',
    description: '',
    policy_pack_id: '',
  });
  const [showPolicyDetails, setShowPolicyDetails] = useState(false);
  const [selectedPolicyPackSummary, setSelectedPolicyPackSummary] = useState<any>(null);
  const [policyPackDetails, setPolicyPackDetails] = useState<PolicyPack | null>(null);
  const [loadingPolicyDetails, setLoadingPolicyDetails] = useState(false);
  const [editedPolicyData, setEditedPolicyData] = useState<string>('');

  // Fetch projects
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', tenantId],
    queryFn: () => listProjects(tenantId || undefined),
    enabled: !!tenantId && isAuthenticated,
  });

  // Sync projects to store when they're loaded
  useEffect(() => {
    if (projects) {
      setProjects(projects);
    }
  }, [projects, setProjects]);

  // Fetch tenants
  const { data: tenants, isLoading: tenantsLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => listTenants(),
    enabled: isAuthenticated,
  });

  // Fetch policy packs (fetch all packs, not filtered by tenant)
  const { data: policyPacks } = useQuery({
    queryKey: ['policy-packs'],
    queryFn: () => listPolicyPacks(),
    enabled: isAuthenticated,
    staleTime: 300000, // 5 minutes
  });

  // Fetch sources for account setup check
  const { data: sources, isLoading: sourcesLoading } = useQuery({
    queryKey: ['sources', projectId],
    queryFn: () => listSources(projectId!),
    enabled: !!projectId && isAuthenticated,
  });

  // Fetch stats for all projects in a single query
  const projectIds = projects?.map(p => p.id) || [];
  const { data: allProjectStats, isLoading: statsLoading } = useQuery({
    queryKey: ['all-project-stats', projectIds.join(',')],
    queryFn: async () => {
      if (!projectIds.length) return {};
      const statsPromises = projectIds.map(id => 
        getProjectStats(id).catch((err) => {
          console.warn(`Failed to fetch stats for project ${id}:`, err);
          return null;
        })
      );
      const statsArray = await Promise.all(statsPromises);
      return Object.fromEntries(
        projectIds.map((id, idx) => [id, statsArray[idx]])
      );
    },
    enabled: projectIds.length > 0 && isAuthenticated,
    staleTime: 30000, // 30 seconds
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Create project mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => createProject({
      ...data,
      tenant_id: tenantId,
      policy_pack_id: data.policy_pack_id || null,
      configuration: {},
    }),
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setShowCreateModal(false);
      setNewProject({ name: '', slug: '', description: '', policy_pack_id: '' });
      // Navigate to new project
      setProjectId(project.id);
    },
  });

  const handleCreateProject = () => {
    if (!newProject.name || !newProject.slug) {
      alert('Project name and slug are required');
      return;
    }
    createMutation.mutate(newProject);
  };

  const handlePolicyPackSelect = async (packId: string) => {
    setNewProject({ ...newProject, policy_pack_id: packId });
    const pack = policyPacks?.find(p => p.id === packId);
    setSelectedPolicyPackSummary(pack || null);
    
    // Fetch full policy pack details if a pack is selected
    if (packId && pack) {
      setLoadingPolicyDetails(true);
      try {
        const fullPack = await getPolicyPack(packId);
        setPolicyPackDetails(fullPack);
        setEditedPolicyData(JSON.stringify(fullPack.policy_data || {}, null, 2));
      } catch (error) {
        console.error('Failed to load policy pack details:', error);
      } finally {
        setLoadingPolicyDetails(false);
      }
    } else {
      setPolicyPackDetails(null);
      setEditedPolicyData('');
    }
  };

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setNewProject({
      ...newProject,
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
    });
  };

  // Navigation helper
  const baseUrl = getBaseUrl();
  const handleNavigate = (path: string) => {
    const fullPath = baseUrl === '/' ? path : baseUrl.replace(/\/$/, '') + path;
    window.history.pushState({}, '', fullPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // ============================================================================
  // FEATURE DISCOVERY
  // ============================================================================

  // Get all available features from baseline and plugins
  // Force re-computation when plugins are registered
  const [pluginLoadKey, setPluginLoadKey] = useState(0);
  
  useEffect(() => {
    // Force plugin registry to load and trigger re-render
    const checkPlugins = () => {
      const plugins = pluginRegistry.getAllPlugins();
      if (plugins.length > 0) {
        setPluginLoadKey(prev => prev + 1);
      }
    };
    
    // Check immediately
    checkPlugins();
    
    // Also check after a short delay to catch async plugin loading
    const timeout = setTimeout(checkPlugins, 100);
    
    // Listen for plugin registration events
    const handlePluginReady = () => {
      checkPlugins();
    };
    
    window.addEventListener('tracekitConfigReady', handlePluginReady);
    
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('tracekitConfigReady', handlePluginReady);
    };
  }, []);

  const availableFeatures = useMemo(() => {
    const features: Array<{
      id: string;
      category: 'baseline' | 'plugin';
      pluginName?: string;
      label: string;
      path: string;
      icon: React.ReactNode;
      description?: string;
    }> = [];

    // Plugins that replace core navigation items (scalable for future plugins)
    const PLUGINS_THAT_REPLACE_REPORTS = ['rfp-agent'];
    const PLUGINS_THAT_REPLACE_DATA_MANAGEMENT = ['rfp-agent'];
    
    // Check if any plugin that replaces reports is active
    const hasReportsReplacement = PLUGINS_THAT_REPLACE_REPORTS.some(pluginId => 
      pluginRegistry.getPlugin(pluginId) !== undefined
    );
    
    // Check if any plugin that replaces data management is active
    const hasDataManagementReplacement = PLUGINS_THAT_REPLACE_DATA_MANAGEMENT.some(pluginId => 
      pluginRegistry.getPlugin(pluginId) !== undefined
    );

    // Baseline features (always available)
    features.push(
      {
        id: 'search',
        category: 'baseline',
        label: 'Universal AI Search',
        path: '/search',
        icon: <Search className="h-5 w-5" />,
        description: 'AI-powered semantic search across all data',
      },
      {
        id: 'chat',
        category: 'baseline',
        label: 'AI Chat',
        path: '/chat',
        icon: <MessageSquare className="h-5 w-5" />,
        description: 'Conversational AI interface for Q&A',
      },
      // Conditionally include Data Pipeline only if no plugin replaces it
      ...(hasDataManagementReplacement ? [] : [{
        id: 'data-management',
        category: 'baseline',
        label: 'Data Pipeline',
        path: '/data',
        icon: <Database className="h-5 w-5" />,
        description: 'Manage sources, knowledge, jobs, entities, and chat',
      }]),
      {
        id: 'knowledge',
        category: 'baseline',
        label: 'Knowledge Base',
        path: '/knowledge',
        icon: <Package className="h-5 w-5" />,
        description: 'Document management and search',
      },
      {
        id: 'entities',
        category: 'baseline',
        label: 'Entity Explorer',
        path: '/entities',
        icon: <Layers className="h-5 w-5" />,
        description: 'Browse and query extracted entities',
      },
      // Conditionally include Reports only if no plugin replaces it
      ...(hasReportsReplacement ? [] : [{
        id: 'reports',
        category: 'baseline',
        label: 'Reports',
        path: '/reports',
        icon: <FileText className="h-5 w-5" />,
        description: 'Generate AI-powered reports',
      }])
      // Settings removed from home page feature discovery
    );

    // Plugin features
    const plugins = pluginRegistry.getAllPlugins();
    plugins.forEach((plugin) => {
      if (plugin.sidebarItems) {
        plugin.sidebarItems.forEach((item) => {
          features.push({
            id: `${plugin.id}-${item.path}`,
            category: 'plugin',
            pluginName: plugin.name,
            label: item.label,
            path: item.path,
            icon: item.icon ? <item.icon className="h-5 w-5" /> : <Briefcase className="h-5 w-5" />,
            description: (item as any).description || `${plugin.description}`,
          });
        });
      }
    });

    return features;
  }, [pluginLoadKey]); // Re-compute when plugins load

  // ============================================================================
  // ACCOUNT SETUP CHECKLIST
  // ============================================================================

  interface SetupStep {
    id: string;
    label: string;
    description: string;
    completed: boolean;
    action?: { label: string; path: string };
    icon: React.ReactNode;
  }

  const setupSteps = useMemo((): SetupStep[] => {
    const hasTenant = !!tenantId && !!tenant;
    const hasProject = !!projectId && !!project;
    const hasProjects = (projects?.length || 0) > 0;
    const hasArtifacts = hasProject && (allProjectStats?.[projectId]?.total_artifacts || 0) > 0;
    const hasSources = hasProject && (sources?.length || 0) > 0;
    const hasPolicyPack = hasProject && !!project?.policy_pack_id;
    const hasTenantConfig = hasCustomConfig;

    return [
      {
        id: 'tenant',
        label: 'Select Tenant',
        description: 'Choose your organization or client workspace',
        completed: hasTenant,
        icon: <Database className="h-5 w-5" />,
      },
      {
        id: 'project',
        label: 'Create or Select Project',
        description: 'Set up at least one project to organize your data',
        completed: hasProjects,
        action: hasProjects ? undefined : { label: 'Create Project', path: '/' },
        icon: <Folder className="h-5 w-5" />,
      },
      {
        id: 'sources',
        label: 'Add Data Sources',
        description: 'Connect databases or upload documents',
        completed: hasSources || hasArtifacts,
        action: hasSources || hasArtifacts ? undefined : { label: 'Add Source', path: '/data?tab=sources' },
        icon: <Layers className="h-5 w-5" />,
      },
      {
        id: 'policy',
        label: 'Configure Policy Pack (Optional)',
        description: 'Set up extraction policies and compliance rules',
        completed: hasPolicyPack,
        action: hasPolicyPack ? undefined : { label: 'View Policies', path: '/admin?tab=policies' },
        icon: <Shield className="h-5 w-5" />,
      },
      {
        id: 'tenant-config',
        label: 'Configure Tenant Resources (Optional)',
        description: 'Set up custom database, storage, and AI services',
        completed: hasTenantConfig,
        action: hasTenantConfig ? undefined : { label: 'Configure', path: '/admin?tab=tenant-config' },
        icon: <Settings className="h-5 w-5" />,
      },
    ];
  }, [tenantId, tenant, projectId, project, projects, allProjectStats, sources, hasCustomConfig]);

  const completedSteps = setupSteps.filter((s) => s.completed).length;
  const totalSteps = setupSteps.length;
  const setupProgress = Math.round((completedSteps / totalSteps) * 100);
  
  // Enterprise-ready: Only show setup checklist after data has actually loaded
  // Don't show it while queries are still loading to avoid inconsistent UI
  const isDataLoading = tenantsLoading || projectsLoading || (!!projectId && sourcesLoading);
  const hasDataLoaded = !isDataLoading && tenants !== undefined && projects !== undefined;
  
  // Show setup section only if:
  // 1. Data has finished loading (not just undefined)
  // 2. Less than 3 steps are completed
  const needsSetup = hasDataLoaded && completedSteps < 3;

  // Show simplified dashboard for Viewer/Writer roles without data management access
  if (shouldShowSimplified) {
    return <SimplifiedDashboard />;
  }

  return (
    <div className="space-y-8">
      {/* Tenant Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-2xl p-8 text-white shadow-2xl">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-lg">
              <Database className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold">{tenant?.name || 'TraceOS'}</h1>
              <p className="text-blue-100 text-lg mt-1">
                {projects?.length || 0} project{projects?.length !== 1 ? 's' : ''} • {tenantId?.substring(0, 8)}...
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Setup Section - Show if missing critical setup */}
      {needsSetup && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-6 border-2 border-amber-200 dark:border-amber-700">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 dark:bg-amber-800 rounded-lg">
              <Rocket className="h-6 w-6 text-amber-600 dark:text-amber-300" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Get Started with TraceOS</h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                    Complete these steps to unlock the full potential of your workspace
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{setupProgress}%</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">{completedSteps}/{totalSteps} completed</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full h-2 bg-amber-100 dark:bg-amber-900 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full bg-amber-500 dark:bg-amber-400 transition-all duration-500"
                  style={{ width: `${setupProgress}%` }}
                />
              </div>

              {/* Setup Steps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {setupSteps.map((step) => (
                  <div
                    key={step.id}
                    className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                      step.completed
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className={`${step.completed ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                      {step.completed ? <CheckCircle className="h-5 w-5" /> : step.icon}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{step.label}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{step.description}</div>
                      {!step.completed && step.action && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleNavigate(step.action!.path)}
                          className="mt-2 text-xs"
                        >
                          {step.action.label}
                          <ArrowRight className="h-3 w-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feature Discovery Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Available Features</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Explore all features available in your workspace
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowHelpCenter(true)}
            className="flex items-center gap-2"
          >
            <HelpCircle className="h-4 w-4" />
            Help Center
          </Button>
        </div>

        {/* Baseline Features */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            Core Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {availableFeatures
              .filter((f) => f.category === 'baseline')
              .map((feature) => (
                <div
                  key={feature.id}
                  onClick={() => handleNavigate(feature.path)}
                  className="cursor-pointer"
                >
                  <Card className="hover:shadow-md transition-all border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400">
                    <div className="flex items-start gap-3">
                      <div className="text-blue-600 dark:text-blue-400">{feature.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                          {feature.label}
                        </div>
                        {feature.description && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {feature.description}
                          </div>
                        )}
                      </div>
                      <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </Card>
                </div>
              ))}
          </div>
        </div>

        {/* Plugin Features */}
        {availableFeatures.filter((f) => f.category === 'plugin').length > 0 && (() => {
          const pluginFeatures = availableFeatures.filter((f) => f.category === 'plugin');
          // Get unique plugin names
          const pluginNames = [...new Set(pluginFeatures.map(f => f.pluginName).filter(Boolean))];
          // Use the plugin name if there's only one, otherwise show "Plugin Features"
          const headingText = pluginNames.length === 1 ? pluginNames[0] : 'Plugin Features';
          
          return (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-purple-600" />
                {headingText}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {availableFeatures
                  .filter((f) => f.category === 'plugin')
                  .map((feature) => (
                    <div
                      key={feature.id}
                      onClick={() => handleNavigate(feature.path)}
                      className="cursor-pointer"
                    >
                      <Card className="hover:shadow-md transition-all border-2 border-gray-200 dark:border-gray-700 hover:border-purple-400">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-start gap-3">
                            <div className="text-purple-600 dark:text-purple-400">{feature.icon}</div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                                {feature.label}
                              </div>
                              {feature.description && (
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                  {feature.description}
                                </div>
                              )}
                            </div>
                            <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          </div>
                          {feature.pluginName && (
                            <Badge variant="info" className="text-xs w-fit">
                              {feature.pluginName}
                            </Badge>
                          )}
                        </div>
                      </Card>
                    </div>
                  ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Projects Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Projects</h2>
            <p className="text-gray-600 mt-1">Manage your data projects and workspaces</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>

        {/* Project Cards Grid */}
        {projectsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-48 bg-gray-200 rounded"></div>
              </Card>
            ))}
          </div>
        ) : projects && projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                stats={allProjectStats?.[project.id] || null}
                statsLoading={statsLoading}
                onNavigate={setProjectId}
              />
            ))}
          </div>
        ) : (
          <Card>
            <div className="text-center py-12">
              <Folder className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No projects yet</p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Project
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" 
             onClick={() => setShowCreateModal(false)}>
          <div className="max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <Card>
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create New Project</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Name *
                    </label>
                    <Input
                      value={newProject.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="e.g., PFAS CFB Comox"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slug * <span className="text-gray-500 font-normal">(auto-generated)</span>
                    </label>
                    <Input
                      value={newProject.slug}
                      onChange={(e) => setNewProject({ ...newProject, slug: e.target.value })}
                      placeholder="pfas-cfb-comox"
                      pattern="[a-z0-9-]+"
                      className="w-full font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lowercase letters, numbers, and hyphens only
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={newProject.description}
                      onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                      placeholder="Describe the purpose of this project..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Shield className="inline h-4 w-4 mr-1" />
                      Policy Pack (Optional)
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={newProject.policy_pack_id}
                        onChange={(e) => handlePolicyPackSelect(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No policy pack</option>
                        {policyPacks?.map((pack) => (
                          <option key={pack.id} value={pack.id}>
                            {pack.name} (v{pack.version})
                          </option>
                        ))}
                      </select>
                      {newProject.policy_pack_id && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowPolicyDetails(!showPolicyDetails)}
                        >
                          {showPolicyDetails ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          {showPolicyDetails ? 'Hide' : 'Show'} Details
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Configure content policies, evidence requirements, and compliance rules
                    </p>
                    
                    {/* Policy Pack Details Section */}
                    {newProject.policy_pack_id && showPolicyDetails && (
                      <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                        {loadingPolicyDetails ? (
                          <div className="p-4 text-center text-gray-500">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                            Loading policy details...
                          </div>
                        ) : policyPackDetails ? (
                          <div className="p-3">
                            <PolicyPackViewer policyPack={{
                              ...policyPackDetails,
                              description: policyPackDetails.description || '',
                              disclosure_text: policyPackDetails.disclosure_text || undefined
                            }} />
                            
                            {/* Advanced JSON Editor */}
                            <details className="mt-4 border-t border-gray-200 pt-4">
                              <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2">
                                <Edit3 className="h-4 w-4" />
                                Edit Full Configuration (Advanced)
                              </summary>
                              <div className="mt-3">
                                <textarea
                                  value={editedPolicyData}
                                  onChange={(e) => setEditedPolicyData(e.target.value)}
                                  rows={15}
                                  className="w-full px-3 py-2 border border-gray-300 rounded text-xs font-mono bg-gray-50 dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100"
                                  placeholder="Policy configuration JSON..."
                                />
                                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                  <AlertCircle className="h-4 w-4" />
                                  Note: Editing policy configuration requires advanced knowledge. Changes will be applied to this project only.
                                </p>
                              </div>
                            </details>
                          </div>
                        ) : (
                          <div className="p-4 text-center text-gray-500">
                            Failed to load policy details
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1"
                    disabled={createMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateProject}
                    disabled={!newProject.name || !newProject.slug || createMutation.isPending}
                    className="flex-1"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Project'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Help Center Modal */}
      <HelpCenter 
        isOpen={showHelpCenter} 
        onClose={() => setShowHelpCenter(false)}
        supportEmail="support@alpyne.ai"
      />
    </div>
  );
}

